import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { ROLES } from '@bcc/shared';
import { SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

type PromptPayload = {
  system_prompt: string;
  source: 'database' | 'builtin';
  updated_at: string | null;
};

const MAX_CHARS = 120_000;

/** Mirrors `dealAnalyzerOutputSchema` — editing the prompt must keep responses compatible with these sections. */
const OUTPUT_REFERENCE = `Dashboard expects one JSON object with (at minimum) these top-level keys:
• assumptions — financing inputs (cashPurchase, purchasePrice, loan fields, growth %, etc.)
• property — address, units, unitMix, totals
• extractedLineItems — income[] and operatingExpenses[] line items
• purchaseInvestment — table rows with item, amount, perSf, bold
• cashFlowSummary — rows with label, annual, perSfYr, kind (revenue | deduction | subtotal | total)
• exitAnalysis — exit pricing, deductions, proceeds
• returnsSummary — equity, cap rate, CoC, IRR, equity multiple
• kpis — label, value, tone (positive | negative | neutral)
• normalizationReportMarkdown — Markdown diligence narrative
• sensitivity — entryCapRates, exitCapLabels, irrGrid[][], highlight indices
• projections — yearly NOIs, debt service, cash flow, CoC%, donuts

The server validates AI output with dealAnalyzerOutputSchema; omitting shape guidance risks failed analyses.`;

/** Admin-only — system prompt stored in site_content; applies to every Deal Analyzer request. */
export function AdminDealAnalyzerPrompt() {
  const { session, role } = useAuth();
  const token = session?.access_token;
  const [payload, setPayload] = useState<PromptPayload | null>(null);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token || role !== ROLES.ADMIN) return;
    setErr(null);
    try {
      const res = await apiFetch<PromptPayload>('/api/admin/deal-analyzer-prompt', { token });
      setPayload(res);
      setDraft(res.system_prompt);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, role]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      const next = await apiFetch<PromptPayload>('/api/admin/deal-analyzer-prompt', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ system_prompt: draft }),
      });
      setPayload(next);
      setDraft(next.system_prompt);
      setOk('Saved. All Deal Analyzer runs will use this system prompt.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function onReset() {
    if (!token) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      const next = await apiFetch<PromptPayload>('/api/admin/deal-analyzer-prompt', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ reset_to_default: true }),
      });
      setPayload(next);
      setDraft(next.system_prompt);
      setOk('Restored built-in default prompt from the server.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (role !== ROLES.ADMIN) {
    return (
      <div className={`p-6 ${SITE_GLASS_CARD}`}>
        <p className="text-sm text-[var(--home-muted-on-dark)]">Only administrators can edit the deal analyzer prompt.</p>
      </div>
    );
  }

  const overLimit = draft.length > MAX_CHARS;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-body text-2xl font-semibold text-[var(--home-text-on-dark)]">Deal Analyzer — AI prompt</h1>
        <p className="mt-2 max-w-3xl text-sm text-[var(--home-muted-on-dark)]">
          This system prompt is saved in your Postgres database and sent to the model for{' '}
          <strong className="text-[var(--home-text-on-dark)]">every</strong> investor Deal Analyzer run (OpenAI or Anthropic). User
          messages still include uploaded documents and their assumption JSON. Keep instructions aligned with the JSON structure
          below so responses validate.
        </p>
      </div>

      <details className={`${SITE_GLASS_CARD} open:ring-1 open:ring-white/10`}>
        <summary className="cursor-pointer select-none px-5 py-4 text-sm font-medium text-[var(--home-text-on-dark)]">
          Expected JSON output shape (reference)
        </summary>
        <div className="border-t border-white/[0.08] px-5 pb-5 pt-3 font-mono text-[11px] leading-relaxed text-[var(--home-muted-on-dark)] whitespace-pre-wrap">
          {OUTPUT_REFERENCE}
        </div>
      </details>

      <div className={`p-6 ${SITE_GLASS_CARD}`}>
        <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-white/[0.08] pb-4">
          <span className="text-sm font-medium text-[var(--home-text-on-dark)]">Active source</span>
          <span
            className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
              payload?.source === 'database' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-white/10 text-[var(--home-muted-on-dark)]'
            }`}
          >
            {payload?.source === 'database' ? 'Database' : 'Built-in default'}
          </span>
          {payload?.updated_at ? (
            <span className="text-xs text-[var(--home-muted-on-dark)]">
              Updated {new Date(payload.updated_at).toLocaleString()}
            </span>
          ) : null}
        </div>

        {err ? <p className="mb-4 text-sm text-red-400">{err}</p> : null}
        {ok ? <p className="mb-4 text-sm text-emerald-400">{ok}</p> : null}

        <form className="space-y-4" onSubmit={onSave}>
          <label className="block space-y-2">
            <span className="text-xs text-[var(--home-muted-on-dark)]">
              System prompt <span className="text-[var(--home-text-on-dark)]">({draft.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters)</span>
            </span>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              rows={22}
              className={`w-full resize-y rounded-xl border border-white/15 bg-black/25 px-4 py-3 font-mono text-xs leading-relaxed text-[var(--home-text-on-dark)] outline-none ring-[var(--home-miami-blue)]/35 placeholder:text-white/35 focus:border-[var(--home-miami-blue)]/50 focus:ring-2 ${
                overLimit ? 'border-red-400/50' : ''
              }`}
            />
          </label>
          {overLimit ? <p className="text-sm text-red-400">Prompt exceeds maximum length.</p> : null}

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={loading || !token || overLimit || draft.trim().length === 0}
              className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105 disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Save prompt'}
            </button>
            <button
              type="button"
              disabled={loading || !token || payload?.source !== 'database'}
              onClick={() => void onReset()}
              className="rounded-xl border border-white/15 px-5 py-2.5 text-sm font-medium text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Reset to built-in default
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
